import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LegalDocumentModal from '../common/LegalDocumentModal';
import { RiderTermsContent } from '../../pages/RiderTermsOfUse';
import { PrivacyPolicyContent } from '../../pages/PrivacyPolicy';
import { getUserTimezone } from '../../utils/dateUtils';


const RiderSignup = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'rider_signup_start');
    }
  }, []);

  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    date_of_birth: '',
    tou_accepted: false,
    privacy_accepted: false,
    email_notifications: true,
    sms_notifications: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Modal states
  const [showTouModal, setShowTouModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [touViewed, setTouViewed] = useState(false);
  const [privacyViewed, setPrivacyViewed] = useState(false);
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTouAccept = () => {
    setTouViewed(true);
    setFormData(prev => ({ ...prev, tou_accepted: true }));
    setShowTouModal(false);
  };

  const handlePrivacyAccept = () => {
    setPrivacyViewed(true);
    setFormData(prev => ({ ...prev, privacy_accepted: true }));
    setShowPrivacyModal(false);
  };

  // Helper function to clean form data
  const cleanFormData = (data) => {
    return {
      email: data.email && data.email.trim() !== '' ? data.email.trim() : null,
      phone: data.phone && data.phone.trim() !== '' ? data.phone.trim() : null,
      password: data.password,
      first_name: data.first_name.trim(),
      last_name: data.last_name.trim(),
      date_of_birth: data.date_of_birth,
      tou_accepted: data.tou_accepted,
      privacy_accepted: data.privacy_accepted,
      email_notifications: data.email_notifications,
      sms_notifications: data.sms_notifications,
      utm_source: sessionStorage.getItem('utm_source'),
      utm_medium: sessionStorage.getItem('utm_medium'),
      utm_campaign: sessionStorage.getItem('utm_campaign'),
      utm_content: sessionStorage.getItem('utm_content'),
      utm_term: sessionStorage.getItem('utm_term'),
      landing_page: sessionStorage.getItem('landing_page'),
      timezone: getUserTimezone()
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation - both email and phone are required
    if (!formData.email) {
      setError('Email address is required');
      return;
    }

    if (!formData.phone) {
      setError('Phone number is required');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    // Age verification - riders must be 18+
    if (!formData.date_of_birth) {
      setError('Date of birth is required');
      return;
    }

    const birthDate = new Date(formData.date_of_birth);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    const dayDiff = today.getDate() - birthDate.getDate();

    // Adjust age if birthday hasn't occurred yet this year
    const actualAge = (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) ? age - 1 : age;

    if (actualAge < 18) {
      setError('You must be at least 18 years old to use this service');
      return;
    }

    if (!formData.tou_accepted) {
      setError('You must accept the Rider Terms of Use');
      return;
    }

    if (!formData.privacy_accepted) {
      setError('You must accept the Privacy Policy');
      return;
    }

    setLoading(true);

    try {
      // Get driver_id from URL if present
      const driverId = searchParams.get('driver_id');

      // Store driver_id in sessionStorage for redirect after signup
      if (driverId) {
        sessionStorage.setItem('intended_driver_id', driverId);
      }

      const signupData = cleanFormData(formData);

      // Add referred_by_driver if present
      if (driverId) {
        signupData.referred_by_driver = parseInt(driverId);
      }

      // Log the data being sent for debugging
      console.log('📤 Sending signup data:', signupData);
      console.log('📤 Email value:', signupData.email, 'Type:', typeof signupData.email);
      console.log('📤 Phone value:', signupData.phone, 'Type:', typeof signupData.phone);
      if (driverId) {
        console.log('📤 Referred by driver:', driverId);
      }

      const result = await signup(signupData, 'rider');

      if (result.success) {
        // Fire tracking events
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'rider_signup_complete');
        }
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'CompleteRegistration', { content_name: 'rider' });
        }
        if (typeof window.rdt === 'function') {
          window.rdt('track', 'SignUp');
        }
        // Determine where to navigate
        const destination = driverId
          ? `/request-ride?driver_id=${driverId}`
          : '/rider/dashboard';

        navigate(destination);
      } else {
        // Fix: Properly handle error objects
        if (typeof result.error === 'string') {
          setError(result.error);
        } else if (result.error?.detail) {
          // Handle FastAPI HTTPException
          if (Array.isArray(result.error.detail)) {
            // Handle Pydantic validation errors
            const errorMessages = result.error.detail.map(err =>
              `${err.loc.join(' -> ')}: ${err.msg}`
            ).join('; ');
            setError(errorMessages);
          } else if (typeof result.error.detail === 'string') {
            setError(result.error.detail);
          } else {
            setError('Signup failed. Please check your information.');
          }
        } else {
          setError('Signup failed. Please try again.');
        }
      }
    } catch (err) {
      console.error('Signup error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 py-12">
      <div className="bg-white p-8 rounded-lg shadow-2xl w-full max-w-2xl">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">
          Rider Sign Up
        </h2>

        {/* Email Verification Notice */}
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> You'll need to verify your email or phone number to receive notifications about your rides.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                First Name *
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Last Name *
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="your.email@example.com"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="+1234567890"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Required for drivers to contact you about scheduled rides</p>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Date of Birth *
            </label>
            <input
              type="date"
              name="date_of_birth"
              value={formData.date_of_birth}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <p className="text-xs text-gray-500 mt-1">You must be at least 18 years old to use this service</p>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Password *
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              minLength={8}
            />
            <p className="text-xs text-gray-500 mt-1">At least 8 characters</p>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Confirm Password *
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Notification Preferences Section */}
          <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Notification Preferences</h3>
            <p className="text-xs text-gray-500 mb-3">Choose how you'd like to receive updates about your rides</p>

            <div className="space-y-3">
              {/* Email - Required */}
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="email_notifications"
                  checked={true}
                  disabled
                  className="mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-not-allowed"
                />
                <span className="text-sm text-gray-700">
                  Email notifications <span className="text-red-500">*</span>
                </span>
                <span className="ml-2 text-xs text-gray-500">(Required)</span>
              </label>

              {/* SMS - Coming Soon */}
              <label className="flex items-center opacity-50 cursor-not-allowed">
                <input
                  type="checkbox"
                  name="sms_notifications"
                  checked={false}
                  disabled
                  className="mr-3 h-4 w-4 text-gray-400 border-gray-300 rounded cursor-not-allowed"
                />
                <span className="text-sm text-gray-400">
                  SMS/Text notifications
                </span>
                <span className="ml-2 text-xs text-amber-600 font-medium">(Coming soon)</span>
              </label>
            </div>
          </div>

          {/* Terms of Use Section */}
          <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Legal Agreements</h3>

            {/* Rider Terms of Use */}
            <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="tou_accepted"
                  checked={formData.tou_accepted}
                  onChange={handleChange}
                  disabled={!touViewed}
                  className="mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                />
                <span className={`text-sm ${!touViewed ? 'text-gray-400' : 'text-gray-700'}`}>
                  I agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setShowTouModal(true)}
                    className="text-blue-600 hover:text-blue-700 font-medium underline"
                  >
                    Rider Terms of Use
                  </button>
                </span>
              </div>
              {!touViewed && (
                <button
                  type="button"
                  onClick={() => setShowTouModal(true)}
                  className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full hover:bg-blue-200 transition"
                >
                  View & Accept
                </button>
              )}
              {touViewed && formData.tou_accepted && (
                <span className="text-xs text-blue-600 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Accepted
                </span>
              )}
            </div>

            {/* Privacy Policy */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="privacy_accepted"
                  checked={formData.privacy_accepted}
                  onChange={handleChange}
                  disabled={!privacyViewed}
                  className="mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                />
                <span className={`text-sm ${!privacyViewed ? 'text-gray-400' : 'text-gray-700'}`}>
                  I agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setShowPrivacyModal(true)}
                    className="text-blue-600 hover:text-blue-700 font-medium underline"
                  >
                    Privacy Policy
                  </button>
                </span>
              </div>
              {!privacyViewed && (
                <button
                  type="button"
                  onClick={() => setShowPrivacyModal(true)}
                  className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full hover:bg-blue-200 transition"
                >
                  View & Accept
                </button>
              )}
              {privacyViewed && formData.privacy_accepted && (
                <span className="text-xs text-blue-600 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Accepted
                </span>
              )}
            </div>

            {(!touViewed || !privacyViewed) && (
              <p className="text-xs text-gray-500 mt-3 italic">
                You must view and accept each document before signing up
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || !formData.tou_accepted || !formData.privacy_accepted}
            className="w-full bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 transition duration-200 font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating Account...' : 'Sign Up'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-500 hover:underline font-medium">
              Login
            </Link>
          </p>
        </div>
      </div>

      {/* Terms of Use Modal */}
      <LegalDocumentModal
        isOpen={showTouModal}
        onClose={() => setShowTouModal(false)}
        onAccept={handleTouAccept}
        title="Rider Terms of Use"
        acceptButtonText="I Accept"
      >
        <RiderTermsContent />
      </LegalDocumentModal>

      {/* Privacy Policy Modal */}
      <LegalDocumentModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        onAccept={handlePrivacyAccept}
        title="Privacy Policy"
        acceptButtonText="I Accept"
      >
        <PrivacyPolicyContent />
      </LegalDocumentModal>

    </div>
  );
};

export default RiderSignup;
